import PropTypes from 'prop-types';
import React from 'react';
import inside from 'point-in-polygon';

/*
This component works in two phaeses:

# 1: Create a polygon with 5 vertices:

1) [x, y] coordinates of the cursor
4-5) [x, y] coordinates of the 4 corners of the sub menu

# 2: Determine if the cursor is in the polygon

We detect when the cursor leaves the wrapped functional child and pin the last
known cursor coordinate as the first vertex in the polygon, then we detect if the
cursor is within the polygon to set the visibility state of the sub menu

—

This is kind of similar to http://bjk5.com/post/44698559168/breaking-down-amazons-mega-dropdown
But we don't measure the cursor vector to determine if it will intersect with
the left wall of the sub menu. Instead, we use an "is cursor inside" method


 ◤ : cursor

 X : polygon boundary (updated on mouse
 move via requestAnimationFrame)

|XXXXXXXXXXXXXX|XXXXXXXXXXXXXXXXXXXXXXXX
|X◤  Button    |                        X
+--X-----------+                        X
    XXX        |                        X
      XXX   ^  |                        X
        XXX |  |        Sub Menu        X
          XX|  |                        X
            |X |                        X
            |XX|                        X
            | X|                        X
            |  --XXXXXXXXXXXXXXXXXXXXXX--
            |
            |
            |
            | Mouse can move into this
            | space and the Sub Menu will
            | stay open
*/

export default class MenuAim extends React.Component {
  state = {
    isMenuVisible: false,
    isSubMenuOffScreen: false,
  };

  static propTypes = {
    position: PropTypes.oneOf(['left', 'right']),
    direction: PropTypes.oneOf(['up', 'down']),
    getContainer: PropTypes.func,
    menuContainer: PropTypes.node,
    mainMenu: PropTypes.node,
  };

  static defaultProps = {
    position: 'left',
    direction: 'down',
    getContainer: undefined,
    menuContainer: undefined,
    mainMenu: undefined,
  };

  componentWillUnmount() {
    window.removeEventListener('mousemove', this.handleMouseMove, false);
    if (this.timeout) {
      window.cancelAnimationFrame(this.timeout);
    }
  }

  handleMouseMove = data => {
    if (this.timeout) {
      window.cancelAnimationFrame(this.timeout);
    }
    this.timeout = window.requestAnimationFrame(() => {
      const bounding = this.menu?.getBoundingClientRect();
      const mouse = [data.clientX, data.clientY];

      if (!bounding) {
        return;
      }

      if (this.isMouseOutsideParent) {
        this.polygon = {
          mouse: this.polygon?.mouse || mouse, // freeze frame of the last mouse movement.
          menuLeftTop: [bounding.left, bounding.top],
          menuLeftBottom: [bounding.left, bounding.bottom],
          menuRightTop: [bounding.right, bounding.top],
          menuRightBottom: [bounding.right, bounding.bottom],
        };

        // Order matters: if we're rendering a left side menu (the submenu would otherwise be off screen) then we
        // need to "draw" the proper polygon
        const polygon = [
          this.polygon.mouse,
          this.polygon.menuRightTop,
          this.polygon.menuLeftTop,
          this.polygon.menuLeftBottom,
          this.polygon.menuRightBottom,
        ];

        const isMouseInsidePolygon = inside(mouse, polygon);
        if (!isMouseInsidePolygon || this.hasMouseLeftSubMenu) {
          this.setState({
            isMenuVisible: false,
          });
        }
      } else {
        this.polygon = {
          mouse,
          menuLeftTop: [bounding.left, bounding.top],
          menuLeftBottom: [bounding.left, bounding.bottom],
          menuRightTop: [bounding.right, bounding.top],
          menuRightBottom: [bounding.right, bounding.bottom],
        };

        // Once we've moved the menu we don't want to check again since it technically won't be off screen anymore.
        if (!this.state.isSubMenuOffScreen) {
          this.setState({
            isSubMenuOffScreen: bounding.right > window.innerWidth,
          });
        }
      }
    });
  };

  handleMouseEnter = () => {
    this.isMouseOutsideParent = false;
    this.hasMouseLeftSubMenu = false;
    this.setState({
      isMenuVisible: true,
    });

    this.mouseListener = window.addEventListener(
      'mousemove',
      this.handleMouseMove,
      false,
    );
    // We need to know what is the content size before the menu be displayed.
    this.containerScrollHeight = this.props.getContainer()?.scrollHeight;
    // We need to reset any previous direction change
    this.changeMenuDirection(false);
    // Once the menu opens we check if it requires to change direction
    setTimeout(this.checkMenuDirection, 1);
  };

  handleMouseLeave = () => {
    this.isMouseOutsideParent = true;
  };

  handleMouseLeaveSubMenu = () => {
    this.hasMouseLeftSubMenu = true;
    this.isMouseOutsideParent = true;
  };

  changeMenuDirection = (toInvert, newTop) => {
    if (!!this.menu) {
      this.menu.style.top = toInvert ? null : `${newTop}px`;
      this.menu.style.bottom = toInvert ? 0 : null;
    }
  };

  checkMenuDirection = () => {
    let toInvert = false;
    let newTop = null;
    let mainMenu = this.props.mainMenu?.current;
    let menu = this.menu;
    let parentMenu = this.menu?.offsetParent;
    // In case the submenu should be positioned according with the context size and and relation with the parent menu
    if (mainMenu && menu && parentMenu) {
      // We need to know what is the distance of the sub menu related with the scrollable container
      let top = mainMenu.offsetTop + parentMenu.offsetTop;
      newTop = this.containerScrollHeight - (top + menu.offsetHeight + 1);
      toInvert =
        this.containerScrollHeight - top < menu.clientHeight &&
        top > menu.clientHeight;
    } else if (menu) {
      // The positioning is done using the position in the page
      toInvert = menu.getBoundingClientRect().bottom > window.innerHeight;
    }
    this.changeMenuDirection(toInvert, newTop > 0 ? 0 : newTop);
  };

  getMenuStyle = (position, direction) => {
    let style = {
      position: 'absolute',
      display: this.state.isMenuVisible ? 'block' : 'none',
    };
    let menuParentRec = this.menuParent?.getBoundingClientRect();
    let positionOffset =
      menuParentRec &&
      (this.state.isSubMenuOffScreen
        ? -(menuParentRec.width + 2)
        : menuParentRec.width);
    style[direction === 'up' ? 'bottom' : 'top'] = 0;
    style[position === 'right' ? position : 'left'] =
      positionOffset && `${positionOffset}px`;
    return style;
  };

  render() {
    return (
      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: 'inline-block',
            width: '100%',
          }}
          ref={c => (this.menuParent = c)}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
        >
          {this.props.children(this.state.isMenuVisible)}
        </div>
        <div
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeaveSubMenu}
          ref={c => (this.menu = c)}
          style={this.getMenuStyle(this.props.position, this.props.direction)}
        >
          {this.state.isSubMenuOffScreen}
          {this.props.menu}
        </div>
      </div>
    );
  }
}

MenuAim.propTypes = {
  menu: PropTypes.node.isRequired,
  children: PropTypes.func.isRequired,
};
